import type { Card, Copy, Cta, PageMeta } from './types';

/** The client page at /. Verbatim copy; section order is layout order. Visual data is fictional. */
export const client = {
  meta: {
    path: '/',
    name: 'Pairing by Denamico',
    title: 'Pairing by Denamico | HubSpot integrations you can see into',
    description:
      "Pairing keeps HubSpot in sync with Salesforce, NetSuite or your other system. When a record can't sync, it's held with a reason and a fix, then replayed in one click.",
    ogImage: '/og/client.png',
  } satisfies PageMeta,

  hero: {
    eyebrow: 'Pairing by Denamico',
    headline: 'Know immediately when your systems stop talking.',
    subhead:
      'Not three weeks later from marketing. Pairing keeps HubSpot in sync with Salesforce, NetSuite or your other system, and checks every day for changed fields and dropdown values. When something changes, you get a HubSpot task before your lists break.',
    primary: { label: 'Talk to Denamico', href: '#contact' } satisfies Cta,
    secondary: { label: 'Run the free fit check', href: '/fit-check' } satisfies Cta,
    visual: {
      label: 'Example timeline: a picklist change caught and fixed',
      steps: [
        { when: 'Tuesday afternoon', text: "Industry value 'Packaging Machinery' added in Salesforce." },
        { when: 'Overnight', text: "Pairing's daily check spots it." },
        { when: 'Wednesday morning', text: 'HubSpot task' },
        { when: 'Fix', text: 'Mapped and replayed, 14 of 14 synced.' },
      ],
      task: {
        app: 'HubSpot',
        kind: 'Task',
        title: 'Pairing: new Industry value has no match in HubSpot. 14 contacts held.',
        held: '14 held',
        synced: '14 of 14 synced',
      },
    },
  },

  heldRecords: {
    headline: "When a record can't sync, you'll know why.",
    body: "Most integrations fail quietly. A value doesn't match, a required field is empty, or a record looks like two others, and it simply never shows up in HubSpot. Pairing doesn't drop records. It holds each one with a reason in plain language and a suggested fix.",
    steps: [
      { title: 'Held, with a reason.', body: "\"14 contacts held: the Industry value 'Packaging Machinery' has no match in HubSpot.\"" },
      { title: 'Fixed once.', body: 'Add the option to your HubSpot dropdown, or map it to one you already have.' },
      { title: 'Replayed together.', body: 'One click replays every record held for that reason.' },
    ] satisfies Card[],
    supporting:
      "Pairing can also write sync status and the last error onto the HubSpot record itself, so you can build a list of everything that didn't sync and report on it like any other property. And when someone asks why one particular contact never came through, the record trace shows its whole history: what was fetched, how it was transformed and matched, and what was written or why it was held.",
    visual: {
      label: 'Example held records group',
      title: 'Industry values with no mapping',
      pill: '14 held for review',
      oldest: 'Oldest 9 hours ago',
      mapping: 'Salesforce contacts to HubSpot',
      reason: "The Industry value 'Packaging Machinery' has no match in HubSpot.",
      value: 'Packaging Machinery',
      valueCount: '14 records',
      fixes: ['Add the option in HubSpot', 'Map it to an existing value'],
      replay: 'Replay 14 held records',
    },
  },

  /** Shown only when PAIRING_DEMO_VIDEO_URL is set. */
  video: {
    headline: 'Watch Pairing catch a problem.',
    body: { text: 'One held contact, the reason it was held, and the fix.', pending: 'new copy: video section' } satisfies Copy,
    steps: [
      'Connect HubSpot and Salesforce.',
      'A contact is held, with a reason and a suggested fix.',
      'Fix it once and replay every held record in one click.',
      'Get a HubSpot task when a dropdown changes.',
    ],
    prompt: 'Want to see it with your systems?',
    cta: { label: 'Talk to Denamico', href: '#contact' } satisfies Cta,
    title: 'Pairing demo video',
  },

  shadow: {
    headline: 'Run it next to what you have. Switch when the results match.',
    body: "Already have an integration? Custom code, Tray, Zapier, or a native app held together with workarounds. Pairing can run beside it in shadow mode: it reads the same changes, works out what it would write, writes nothing, and reports where it agrees with your current integration and where it doesn't. You decide when to switch.",
    list: [
      'Every mapping is versioned, so any change can be rolled back.',
      'A dry run shows what a change would create, update or hold before it touches either system.',
      "Until your team is handed the keys, Denamico reviews mapping changes before they go live.",
    ],
    visual: {
      label: 'Example shadow report summary. Illustrative numbers.',
      title: 'Shadow report',
      counts: [
        { label: 'Matches', value: '9,412', tone: 'synced' },
        { label: 'Differs', value: '37', tone: 'held' },
        { label: 'Only in Pairing', value: '4', tone: 'info' },
        { label: 'Only in current integration', value: '11', tone: 'neutral' },
      ],
      summary: 'Matches 9,412 · Differs 37 · Only in Pairing 4 · Only in current integration 11',
      difference: {
        pill: 'Differs in 1 field',
        record: 'Contact: Dana Whitfield, Northfield Packaging',
        field: 'Lifecycle stage',
        columns: ['Field', 'Pairing would write', 'Current integration wrote'],
        plumb: 'Sales qualified lead',
        current: 'Lead',
      },
      note: 'Illustrative numbers.',
    },
  },

  capabilities: {
    headline: 'Built for the parts that usually need a developer.',
    cards: [
      {
        title: 'Reshape data on the way in.',
        body: "Value maps for picklists, defaults, date and number conversion, joining and splitting fields, and cleanup for emails, phone numbers and country codes. If configuration can't do it, a Denamico developer writes a small code step that runs in a locked-down sandbox.",
      },
      {
        title: 'Match on your own IDs.',
        body: 'Match by your ERP or account ID, then email, then domain and company name, in the order you choose. If a match is ambiguous, Pairing holds the record instead of guessing.',
      },
      {
        title: 'Associate by unique ID.',
        body: 'Tie contacts to companies by your ERP number without custom-code workflows. It works even when HubSpot is the only system involved.',
      },
      {
        title: 'Keep related records together.',
        body: 'Deal line items travel with their deal to invoice or sales-order lines, and they sync as one unit.',
      },
      {
        title: 'One way or both, on your schedule.',
        body: 'Sync in real time, on a schedule or on demand. When both sides change the same field, you choose who wins: HubSpot, the other system, the latest change, or nobody overwrites.',
      },
      {
        title: 'Big first loads.',
        body: "Ongoing changes go through HubSpot's batch APIs. Large first loads go through HubSpot's imports API.",
      },
      {
        title: 'Clean up before go-live.',
        body: 'A read-only scan finds likely duplicates, inconsistent dropdown values, badly formatted emails and phone numbers, and fields you could fill from the other system. Apply each fix once with undo, or keep applying it going forward.',
      },
    ] satisfies Card[],
  },

  portal: {
    headline: 'You see what your integration is doing. Denamico keeps it running.',
    body: {
      text: "Your team gets its own portal, with your logo and color, showing every run, every held record and every fix. Your admin handles day-to-day fixes there without opening a ticket. Denamico sets up the connections and mappings, reviews changes until you're handed off, and keeps building the product.",
      pending: 'decision 8',
    } satisfies Copy,
    proofLabel: 'About Denamico',
    proof: [
      'Top 1% HubSpot Partner',
      '5x HubSpot CRM accredited, including Custom Integration',
      "We've connected HubSpot to (almost) every ERP and CRM you can name, from Salesforce and NetSuite to Microsoft Dynamics, Oracle, Sage and SQL Server.",
    ],
    dataHeadline: { text: 'Your data stays yours.', pending: 'decision 1' } satisfies Copy,
    dataBody: {
      text: "Each client's data is isolated from every other client's, and credentials and record data are encrypted with keys unique to your account. Sync history is kept for 90 days by default so failed records can be retried; records still waiting on a fix are kept until they're fixed. AI features are off unless you turn them on, and AI assistants never see your record values without your permission.",
      pending: 'decision 1',
    } satisfies Copy,
    visual: {
      label: "Example client portal: a fictional tenant's integrations overview",
      url: 'pairing.denamico.com/t/northfield',
      tenant: 'Northfield Packaging',
      /** A fictional tenant color, deliberately not pear (ADR D17). */
      tenantColor: '#0f6e6e',
      nav: ['Integrations', 'Runs', 'Held records', 'Alerts', 'Connections'],
      heading: 'Integrations',
      description: 'Every sync between HubSpot and your other systems, and how each one is doing.',
      mappings: [
        { name: 'Salesforce contacts', meta: ['Salesforce to HubSpot · Contacts', 'Version 4 live', 'Every 15 minutes'], pills: [{ tone: 'synced', text: 'Synced 6 minutes ago' }] },
        {
          name: 'Salesforce accounts',
          meta: ['Salesforce to HubSpot · Companies', 'Version 2 live', 'Every 15 minutes'],
          pills: [
            { tone: 'synced', text: 'Synced 6 minutes ago' },
            { tone: 'held', text: '14 held for review' },
          ],
        },
        { name: 'NetSuite sales orders', meta: ['NetSuite to HubSpot · Deals', 'Version 1 live', 'Daily at 02:00'], pills: [{ tone: 'synced', text: 'Synced 7 hours ago' }] },
      ],
    },
  },

  notTheAnswer: {
    headline: "When we'll tell you to use something else",
    body: "We'd rather say so now than six months into a project.",
    list: [
      "HubSpot's native integration already covers what you need. Use it; it's free and HubSpot supports it.",
      'Your other system has no API, and no SQL Server database or SFTP or FTPS files to read: EDI or a mainframe. SQL Server and files (read and written) are in pilot; plain FTP and other databases need review.',
      'The process runs across three or more systems, or needs people to approve steps along the way.',
      "You're moving warehouse-scale volume. A purpose-built warehouse sync tool fits better.",
      "HubSpot isn't one of the systems.",
    ],
  },

  faq: {
    headline: 'Questions',
    items: [
      {
        q: 'Do we have to replace our current integration first?',
        a: 'No. Pairing runs beside it in shadow mode and writes nothing to either system. It reports where its results differ from your current integration, and you go live once they match.' as Copy,
      },
      {
        q: 'How long does setup take?',
        a: 'Most setups take one to two weeks. Denamico connects your systems, builds the mappings and runs them in shadow mode before you go live.' as Copy,
      },
      {
        q: 'Can our HubSpot admin run this without a developer?',
        a: 'Day to day, yes. When a record cannot sync, Pairing holds it with a plain-language reason and a suggested fix. Your admin applies the fix in the portal and replays the held records. Denamico handles setup and anything that needs code.' as Copy,
      },
      {
        q: "Our other system isn't Salesforce. Does that matter?",
        a: "Not usually. If it has a modern REST API, Pairing describes it in configuration instead of custom code, and NetSuite is supported too. Not sure? The fit check tells you before you talk to anyone." as Copy,
      },
      {
        q: "What happens to a record that can't sync?",
        a: "Pairing doesn't drop it. The record is held with a plain-language reason and a suggested fix. Fix the cause once, and one click replays every record held for that reason." as Copy,
      },
      {
        q: 'How will we know when something changes?',
        a: 'Pairing checks every day for changed fields and dropdown values. When something changes, you get a HubSpot task before your lists break, and anything that cannot sync is held with a reason.' as Copy,
      },
      {
        q: 'Who can change the mappings?',
        a: 'Your admin can propose a change at any time. Until your team is handed off, Denamico reviews each one before it goes live. Every version is saved, so any change can be rolled back.' as Copy,
      },
      {
        q: 'Is our data secure?',
        a: {
          text: "Each client's data is isolated from every other client's, and credentials and record data are encrypted with keys unique to your account. Sync history is kept for 90 days by default so failed records can be retried; records still waiting on a fix are kept until they're fixed. AI features are off unless you turn them on, and AI assistants never see your record values without your permission.",
          pending: 'decision 1',
        } as Copy,
      },
      {
        q: 'What does it cost?',
        a: "It depends on your systems, how many mappings you need and your record volume, so we scope it on the first call instead of publishing a rate card. Denamico does the setup, the shadow-mode trial comes before you go live, and ongoing support and monitoring are part of the engagement." as Copy,
      },
    ],
  },

  contact: {
    id: 'contact',
    headline: "Tell us what's breaking.",
    body: "Bring the systems you're connecting and what's going wrong today. We'll tell you honestly whether Pairing fits, and what setup would look like for your systems and volume.",
    /** HubSpot's meetings scheduler (islands/BookMeeting.tsx). */
    meetings: {
      href: 'https://meetings.hubspot.com/amy-volatile',
      title: 'Book a time with Denamico',
      open: 'Book a time with Denamico',
      fallbackBefore: "Calendar not loading? ",
      fallbackLabel: 'Open it in a new tab',
    },
    below: { label: 'Run the free fit check', href: '/fit-check', after: '. No sign-in, and it will tell you if Pairing is the wrong tool.', before: 'Not sure yet? ' },
  },
} as const;
